// Emits schema.org JSON-LD for the dealership and its physical locations so
// search engines can surface addresses, phone number and opening hours as
// local rich results / knowledge panel data.
//
// Location data (addresses + opening hours) lives in `#shared/dealer-locations`
// so the print view can reuse it; that module is the machine-readable twin of
// the hours printed in `components/Footer/LowerFooter/index.vue`.

import { DEALER_LOCATIONS, DEALER_TELEPHONE, DEALER_EMAIL, type DealerOpeningHours } from '#shared/dealer-locations';

const TELEPHONE = DEALER_TELEPHONE;
const EMAIL = DEALER_EMAIL;
const LOCATIONS = DEALER_LOCATIONS;

const toOpeningHoursSpecification = (hours: DealerOpeningHours[]) =>
  hours.map(h => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.days.map(d => `https://schema.org/${d}`),
    opens: h.opens,
    closes: h.closes,
  }));

/**
 * Registers a schema.org `@graph` with an Organization node and one AutoDealer
 * node per physical location (each subtyped as LocalBusiness). Call once from
 * the default layout so it renders site-wide.
 */
export const useBusinessSchemaOrg = () => {
  const siteConfig = useSiteConfig();
  const baseUrl = (siteConfig.url || '').replace(/\/$/, '');
  const organizationId = `${baseUrl}/#organization`;

  const graph = [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: 'Herbert Seidl',
      url: baseUrl || undefined,
      telephone: TELEPHONE,
      email: EMAIL,
    },
    ...LOCATIONS.map(location => ({
      '@type': 'AutoDealer',
      '@id': `${baseUrl}/#${location.id}`,
      name: location.name,
      parentOrganization: { '@id': organizationId },
      url: baseUrl || undefined,
      telephone: TELEPHONE,
      email: EMAIL,
      address: {
        '@type': 'PostalAddress',
        streetAddress: location.streetAddress,
        postalCode: location.postalCode,
        addressLocality: location.addressLocality,
        addressCountry: 'AT',
      },
      openingHoursSpecification: toOpeningHoursSpecification(location.openingHours),
    })),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd),
        key: 'business-schema-org',
      },
    ],
  });
};
